"use client"

import { useState } from "react"
import { Loader2, Send } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { UserAvatar } from "@/components/users/UserAvatar"
import { useAuth } from "@/context/AuthContext"
import { useToast } from "@/hooks/useToast"
import { createComment } from "@/lib/db"
import { Link } from "react-router-dom"

interface CommentFormProps {
  postId: string
  parentId?: string
  onSuccess?: () => void
  onCancel?: () => void
  placeholder?: string
  autoFocus?: boolean
}

export function CommentForm({ postId, parentId, onSuccess, onCancel, placeholder = "Write a comment...", autoFocus = false }: CommentFormProps) {
  const { user, userData } = useAuth()
  const { toast } = useToast()
  const [content, setContent] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !content.trim()) return

    setIsSubmitting(true)
    try {
      await createComment({
        postId,
        authorId: user.uid,
        content: content.trim(),
        parentId: parentId ?? null,
        upvoteCount: 0,
        flagCount: 0,
        status: "visible",
      })

      setContent("")
      toast({ title: "Comment posted!" })
      onSuccess?.()
    } catch (error) {
      toast({
        title: "Failed to post comment",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!user) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center dark:border-gray-800 dark:bg-gray-900">
        <p className="text-gray-600 dark:text-gray-400">
          <Link to="/login" className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400">
            Sign in
          </Link>{" "}
          to join the discussion
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-3">
      <UserAvatar src={userData?.avatarUrl} name={userData?.displayName || "User"} size="md" />
      <div className="flex-1 space-y-2">
        <Textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          rows={3}
          className="resize-none"
        />
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" size="sm" disabled={!content.trim() || isSubmitting}>
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            {parentId ? "Reply" : "Comment"}
          </Button>
        </div>
      </div>
    </form>
  )
}
